import React, { useState } from 'react';
import { ParticipantAvatar, UserSelector } from '../../../components/shared';
import { useLanguage } from '../../../hooks/shared';
import { Participant, Training } from '../types/calendarTypes';
import ActionTooltip from './ActionTooltip';

interface ParticipantCardProps {
  participant: Participant;
  training: Training;
  onConfirm: (participantId: string) => Promise<void>;
  onCancel: (participantId: string, withValidExcuse?: boolean) => Promise<void>;
  onReplace: (fromParticipantId: string, toUserId: string) => Promise<void>;
  onDelete: (participantId: string) => Promise<void>;
  availableUsersForReplace: Array<{
    id: string;
    name: string;
    photoUrl?: string;
  }>;
  isFirst?: boolean; // Is this the first participant in the list
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({
  participant,
  training,
  onConfirm,
  onCancel,
  onReplace,
  onDelete,
  availableUsersForReplace,
  isFirst = false,
}) => {
  const { t } = useLanguage();
  const [showReplaceForm, setShowReplaceForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isReplacing, setIsReplacing] = useState(false);

  // Determine participant status icon
  const getStatusIcon = () => {
    switch (participant.status) {
      case 'Confirmed':
      case 'ConfirmedBySystem':
        return '✓';
      case 'Cancelled':
        return '✕';
      case 'Replaced':
        return '↔';
      case 'Pending':
        return '⏳';
      default:
        return '';
    }
  };

  // Determine if actions are available
  // Confirm: for Pending status, or for Cancelled status (to confirm back)
  const canConfirm =
    participant.status === 'Pending' || participant.status === 'Cancelled';

  // Cancel: not available for Cancelled or Deleted status
  // In old logic, cancelled participants were simply not shown in the list
  const canCancel =
    participant.status !== 'Cancelled' && participant.status !== 'Deleted';

  // Replace: available for all statuses except Deleted
  // Can replace cancelled participants (put another participant instead)
  const canReplace = participant.status !== 'Deleted';

  // Delete: admin can always delete
  const canDelete = true;

  // Check if participant is being replaced (has replacing) or is replacing someone (has replacementFor)
  const isBeingReplaced = !!participant.replacing;
  const isReplacingSomeone = !!participant.replacementFor;

  // For cancel action: if participant is replacing someone, we need to cancel the original participant
  // In old CancelParticipantForm, it showed p.replacing ?? p, meaning it showed the original participant
  // So for cancel, we should use the original participant ID if this one is replacing
  const cancelParticipantId =
    isReplacingSomeone && participant.replacementFor
      ? participant.replacementFor
      : participant.id;

  const handleReplace = async () => {
    if (!selectedUserId) return;
    setIsReplacing(true);
    try {
      await onReplace(participant.id, selectedUserId);
      setShowReplaceForm(false);
      setSelectedUserId('');
    } catch (error) {
      console.error('Failed to replace participant:', error);
    } finally {
      setIsReplacing(false);
    }
  };

  return (
    <div
      className="p-3 bg-gray-50 rounded-lg border border-gray-200"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        {/* Participant info */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <ParticipantAvatar
            name={participant.name}
            photoUrl={participant.photoUrl}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 truncate">
                {participant.name}
              </span>
              {getStatusIcon() && (
                <span
                  className="text-sm"
                  title={
                    participant.status === 'Confirmed' ||
                    participant.status === 'ConfirmedBySystem'
                      ? t('calendar.statusConfirmed')
                      : participant.status === 'Cancelled'
                        ? t('calendar.statusCancelled')
                        : participant.status === 'Replaced'
                          ? t('calendar.statusReplaced')
                          : t('calendar.statusPending')
                  }
                >
                  {getStatusIcon()}
                </span>
              )}
            </div>
            {isBeingReplaced && participant.replacing && (
              <div className="text-xs text-gray-500 mt-0.5">
                {t('calendar.replacedBy')}: {participant.replacing.name}
              </div>
            )}
            {isReplacingSomeone &&
              (() => {
                // Find the participant being replaced
                const replacedParticipant = training.participants.find(
                  (p) => p.id === participant.replacementFor
                );
                return replacedParticipant ? (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {t('calendar.replacing')}: {replacedParticipant.name}
                  </div>
                ) : null;
              })()}
          </div>
        </div>

        {/* Action buttons */}
        {!showReplaceForm && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Confirm button */}
            <ActionTooltip
              label={t('common.confirm')}
              disabled={!canConfirm}
              onConfirm={async () => {
                await onConfirm(participant.id);
              }}
              openDown={isFirst}
              variant="default"
            >
              <button
                type="button"
                className={`p-2 rounded transition-colors ${
                  canConfirm
                    ? 'text-green-600 hover:bg-green-100 hover:text-green-700'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                <span className="text-lg">✓</span>
              </button>
            </ActionTooltip>

            {/* Cancel button */}
            <ActionTooltip
              label={t('common.cancelAction')}
              disabled={!canCancel}
              onConfirm={async () => {
                await onCancel(cancelParticipantId);
              }}
              showValidExcuseOption={
                canCancel && !isBeingReplaced && !isReplacingSomeone
              }
              onConfirmWithExcuse={async (withExcuse) => {
                await onCancel(cancelParticipantId, withExcuse);
              }}
              openDown={isFirst}
              variant="default"
            >
              <button
                type="button"
                className={`p-2 rounded transition-colors ${
                  canCancel
                    ? 'text-orange-600 hover:bg-orange-100 hover:text-orange-700'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                <span className="text-lg">✕</span>
              </button>
            </ActionTooltip>

            {/* Replace button - opens form directly */}
            <button
              type="button"
              disabled={!canReplace}
              onClick={() => {
                if (canReplace) {
                  setShowReplaceForm(true);
                }
              }}
              className={`p-2 rounded transition-colors ${
                canReplace
                  ? 'text-blue-600 hover:bg-blue-100 hover:text-blue-700'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
            >
              <span className="text-lg">↔</span>
            </button>

            {/* Delete button */}
            <ActionTooltip
              label={t('common.delete')}
              disabled={!canDelete}
              onConfirm={async () => {
                await onDelete(participant.id);
              }}
              variant="danger"
              openDown={isFirst}
            >
              <button
                type="button"
                className={`p-2 rounded transition-colors ${
                  canDelete
                    ? 'text-red-600 hover:bg-red-100 hover:text-red-700'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                <span className="text-lg">🗑</span>
              </button>
            </ActionTooltip>
          </div>
        )}
      </div>

      {/* Replace form */}
      {showReplaceForm && (
        <div className="mt-3 pt-3 border-t border-gray-300 space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('calendar.replaceTo')}
            </label>
            <UserSelector
              users={availableUsersForReplace}
              value={selectedUserId}
              onChange={setSelectedUserId}
              placeholder={t('calendar.selectParticipant')}
              disabled={isReplacing}
              dropdownDirection={isFirst ? 'down' : 'up'}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowReplaceForm(false);
                setSelectedUserId('');
              }}
              disabled={isReplacing}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleReplace}
              disabled={!selectedUserId || isReplacing}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isReplacing ? t('common.saving') : t('common.confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantCard;
